import React, { useEffect, useRef, useState } from 'react';
import { sponsorAPI, uploadAPI, resolveAssetUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Plus,
  Edit,
  Trash2,
  Loader2,
  Building2,
  Image,
  Save,
  X,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';

export default function Sponsors() {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    tagline: '',
    logo_url: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
  });

  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    try {
      const response = await sponsorAPI.list();
      setSponsors(response.data);
    } catch (error) {
      toast.error('Failed to load sponsors');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (sponsor = null) => {
    if (sponsor) {
      setEditingSponsor(sponsor);
      setFormData({
        name: sponsor.name,
        tagline: sponsor.tagline || '',
        logo_url: sponsor.logo_url || '',
        contact_name: sponsor.contact_name || '',
        contact_email: sponsor.contact_email || '',
        contact_phone: sponsor.contact_phone || '',
        address: sponsor.address || '',
      });
    } else {
      setEditingSponsor(null);
      setFormData({ name: '', tagline: '', logo_url: '', contact_name: '', contact_email: '', contact_phone: '', address: '' });
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingSponsor(null);
    setFormData({ name: '', tagline: '', logo_url: '', contact_name: '', contact_email: '', contact_phone: '', address: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Sponsor name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingSponsor) {
        await sponsorAPI.update(editingSponsor.id, formData);
        toast.success('Sponsor updated');
      } else {
        await sponsorAPI.create(formData);
        toast.success('Sponsor created');
      }
      closeDialog();
      fetchSponsors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sponsor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sponsorId, sponsorName) => {
    if (!window.confirm(`Are you sure you want to delete "${sponsorName}"?`)) {
      return;
    }
    try {
      await sponsorAPI.delete(sponsorId);
      toast.success('Sponsor deleted');
      fetchSponsors();
    } catch (error) {
      toast.error('Failed to delete sponsor');
    }
  };

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5 MB)');
      return;
    }
    setUploadingLogo(true);
    try {
      const res = await uploadAPI.logo(file);
      setFormData((prev) => ({ ...prev, logo_url: res.data.url }));
      toast.success('Logo uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Layout activeNav="sponsors">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Sponsors</h1>
            <p className="text-muted-foreground">Manage event sponsors and generate sponsorship certificates</p>
          </div>
          <Button onClick={() => openDialog()} className="gap-2" data-testid="add-sponsor-btn">
            <Plus className="w-4 h-4" />
            Add Sponsor
          </Button>
        </div>

        {/* Sponsors Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : sponsors.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Building2 className="w-16 h-16 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No sponsors yet</h3>
              <p className="text-muted-foreground mb-6">
                Add sponsors to associate them with your events
              </p>
              <Button onClick={() => openDialog()} className="gap-2">
                <Plus className="w-4 h-4" />
                Add First Sponsor
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="sponsors-grid">
            {sponsors.map((sponsor) => (
              <Card key={sponsor.id} className="border-border">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    {sponsor.logo_url ? (
                      <img 
                        src={resolveAssetUrl(sponsor.logo_url)} 
                        alt={sponsor.name}
                        className="h-12 max-w-[150px] object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDialog(sponsor)}
                        data-testid={`edit-sponsor-${sponsor.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(sponsor.id, sponsor.name)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-sponsor-${sponsor.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-1">{sponsor.name}</h3>
                  {sponsor.tagline && (
                    <p className="text-sm text-muted-foreground italic">{sponsor.tagline}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => !open && closeDialog()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingSponsor ? 'Edit Sponsor' : 'Add New Sponsor'}</DialogTitle>
              <DialogDescription>
                {editingSponsor ? 'Update sponsor details' : 'Enter sponsor information'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Sponsor Name *</Label>
                  <Input
                    id="name"
                    placeholder="Company or Organization Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="sponsor-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tagline">Tagline (Optional)</Label>
                  <Input
                    id="tagline"
                    placeholder="Brief description or motto"
                    value={formData.tagline}
                    onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                    data-testid="sponsor-tagline-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="logo_url">Logo (Optional)</Label>
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                      onChange={handleLogoFileChange}
                      className="hidden"
                      data-testid="sponsor-logo-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="gap-2"
                      data-testid="sponsor-logo-upload-btn"
                    >
                      {uploadingLogo ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      {uploadingLogo ? 'Uploading...' : 'Upload Image'}
                    </Button>
                    <span className="text-xs text-muted-foreground">or paste URL below</span>
                  </div>
                  <Input
                    id="logo_url"
                    placeholder="https://example.com/logo.png"
                    value={formData.logo_url}
                    onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                    data-testid="sponsor-logo-input"
                  />
                  {formData.logo_url && (
                    <div className="mt-2 p-3 bg-muted rounded-lg">
                      <p className="text-xs text-muted-foreground mb-2">Preview:</p>
                      <img 
                        src={resolveAssetUrl(formData.logo_url)} 
                        alt="Logo preview"
                        className="max-h-12"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>

                {/* Contact details (for sponsorship invitation letters) */}
                <div className="pt-2 mt-2 border-t border-border">
                  <p className="text-sm font-medium text-foreground mb-1">Contact details</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Used to address sponsorship invitation letters. Optional but recommended.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="contact_name">Contact name</Label>
                      <Input
                        id="contact_name"
                        placeholder="Jane Doe"
                        value={formData.contact_name}
                        onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                        data-testid="sponsor-contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact_phone">Phone</Label>
                      <Input
                        id="contact_phone"
                        placeholder="+61 2 1234 5678"
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                        data-testid="sponsor-contact-phone-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2 mt-3">
                    <Label htmlFor="contact_email">Email</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      placeholder="contact@sponsor.com"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      data-testid="sponsor-contact-email-input"
                    />
                  </div>
                  <div className="space-y-2 mt-3">
                    <Label htmlFor="address">Address</Label>
                    <Textarea
                      id="address"
                      placeholder="Suite 5, 100 Pitt St&#10;Sydney NSW 2000"
                      rows={2}
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      data-testid="sponsor-address-input"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeDialog}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} className="gap-2" data-testid="save-sponsor-btn">
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {editingSponsor ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
